import { isBefore, parseISO } from 'date-fns';

import {
  DesignToolSeatModel,
  LicenseModel,
} from '../../../../../typings/api/skymap/rest/v0/.common';
import { isDefined } from '../../../utils/variables';
import { ThemeManager } from '../../themes/theme-manager';
import { isIframedByExactStudio } from '../../utils/iframe';
import type { Project } from '../project-state/project-state';

type UserForDesignAccess = {
  isOrganizationAdmin?: boolean;
  companyusers?: string[];
  designToolSeats?: DesignToolSeatModel[];
};

function isDemoPeriodValid(validUntil: string): boolean {
  return isBefore(new Date(), parseISO(validUntil));
}

function isLicenseValid(license: LicenseModel): boolean {
  if (isDefined(license.startedSubscriptionAt)) {
    return true;
  }
  return (license.licenseDemoPeriods ?? []).some((d) => isDemoPeriodValid(d.validUntil));
}

/**
 * Pure: whether the user has a valid design tool seat for the project's company.
 */
export function hasValidDesignToolSeat(
  user: UserForDesignAccess | null | undefined,
  project: Project | null | undefined,
): boolean {
  const seats = user?.designToolSeats ?? [];
  const companyId = project?.companyId;
  if (!isDefined(companyId)) {
    return false;
  }
  const now = Date.now();
  return seats.some((model) => {
    if (model.companyId !== companyId) {
      return false;
    }
    if (model.validUntil === undefined) {
      return true;
    }
    return new Date(model.validUntil).getTime() > now;
  });
}

/**
 * Pure: whether the user has design tool access for the given project/theme.
 * Uses project.companyUserLicenses for DESIGN license check when available.
 */
export function hasDesignToolAccessForCurrentProject(
  user: UserForDesignAccess | null | undefined,
  licenses: LicenseModel[],
  project: (Project & { companyUserLicenses?: LicenseModel[] }) | null | undefined,
  options: {
    themeName?: string;
    isMobaTheme?: boolean;
    isIframedByExactStudio?: boolean;
  } = {},
): boolean {
  if (!user) {
    return false;
  }
  if (user.isOrganizationAdmin) {
    return true;
  }

  const isCompanyAdmin = (user.companyusers ?? []).includes(project?.companyId ?? '');
  const isMoba = options.isMobaTheme ?? ThemeManager.instance.isMobaTheme();
  const iframed = options.isIframedByExactStudio ?? isIframedByExactStudio();

  if (!isCompanyAdmin) {
    return hasValidDesignToolSeat(user, project);
  }

  if (isMoba || project?.isProjectOverridingDesignToolAccess) {
    return true;
  }

  if (!project?.companyId) {
    return false;
  }
  if (hasValidDesignToolSeat(user, project)) {
    return true;
  }
  if (iframed) {
    return true;
  }

  const companyLicenses = project?.companyUserLicenses ?? [];
  const designLicenses = [...companyLicenses, ...licenses].filter(
    (l) => l.licenseType === 'DESIGN' && l.companyId === project.companyId,
  );
  return designLicenses.some(isLicenseValid);
}
